/**
 * 轻量比例识别 — 2026-05-25 自由创作改版。
 *
 * 镜像 ai-service `app/poster/normalize/service.py::_infer_ratio`。
 * 用途:用户**没点 AI 优化**、直接在文案里写了画幅意图(如「竖版」「9:16」「朋友圈」)时,
 * 提交前在前端跑一遍,把比例当作「用户文案显式意图」(最高优先级)采用。
 * AI 优化路径由后端 normalize 返回 suggestedRatio(更鲁棒),走另一条回填。
 *
 * 与后端保持同一套规则,避免两端识别结果不一致。
 */
import type { Ratio } from "./useFreeCreateForm";

/** 显式比例 token(前后不能紧贴数字,避免 "13:40" 命中 "3:4")。 */
const EXPLICIT: ReadonlyArray<[RegExp, Ratio]> = [
  [/(?<!\d)1:1(?!\d)/, "1:1"],
  [/(?<!\d)4:5(?!\d)/, "4:5"],
  [/(?<!\d)3:4(?!\d)/, "3:4"],
  [/(?<!\d)9:16(?!\d)/, "9:16"],
  [/(?<!\d)16:9(?!\d)/, "16:9"],
];

/** 中文方向 / 平台关键词(按顺序取第一个命中)。不含裸「海报」(太泛)。 */
const KEYWORDS: ReadonlyArray<[ReadonlyArray<string>, Ratio]> = [
  [["竖版", "竖图", "竖屏", "长图", "长条"], "9:16"],
  [["横版", "横图", "横屏", "宽幅", "banner", "横幅"], "16:9"],
  // 「正方/方形」有歧义,不进裸词表 —— 见 GATED_SQUARE 注释。「方图」本身含「图」,保留。
  [["方图"], "1:1"],
  // P2-2(0829 琳琳):朋友圈默认 9:16(竖版全屏);显式比例仍优先于关键词(既有优先级)。
  [["朋友圈"], "9:16"],
  [["小红书", "红书"], "3:4"],
  [["公众号头图", "公众号封面"], "16:9"],
  [["易拉宝", "展架", "x展架", "X展架"], "9:16"],
];

/**
 * 「正方/方形」不能当裸关键词:它高频出现在**内容元素描述**里(0831 真机,爱民
 * 任务 5492005929984:备注「每个奖励下面都留一个空白正方形模块」说的是海报里要留个
 * 方块,却被当成整图画幅 → 手选 3:4 被盖成 1:1 出方图)。
 * 只有贴着画幅语境词(同一小句内 ≤6 字,gap 不跨标点)才算画幅意图:「方形海报」
 * 「正方形的图」命中;「正方形模块」「方形图标」不命中(「图」排除「图标/图案」)。
 * 漏掉的交给提交时的 LLM 5 选 1 兜底(它看得懂整段语境)—— 规则层**宁漏不错**:
 * 误命中是确定性覆盖、用户修不回来,漏了还有下一层。
 * ★ai-service `normalize/service.py::_GATED_SQUARE_PATTERN` 是本规则的镜像,改必同改。
 */
const SQUARE_CONTEXT = "(?:画幅|比例|尺寸|构图|版式|海报|画面|图(?![标案])|版|幅)";
const SQUARE_WORD = "(?:正方|方形)";
const SQUARE_GAP = "[^。\\n,，、;；:：!！?？]{0,6}";
const GATED_SQUARE = new RegExp(
  `${SQUARE_CONTEXT}${SQUARE_GAP}${SQUARE_WORD}|${SQUARE_WORD}${SQUARE_GAP}${SQUARE_CONTEXT}`
);

/**
 * 全角→半角归一化:中文 IME 下用户极常打出全角冒号「：」(U+FF1A)与全角数字「０-９」
 * (U+FF10-FF19)。显式比例正则只认半角「9:16」,不归一化会 miss(2026-07-17 生产实锤:
 * 「9：16」出方图)。只归一化冒号 + 数字,与 ai-service `service.py::_infer_ratio` 同规则。
 */
function normalizeFullwidth(text: string): string {
  return text.replace(/[：０-９]/g, (ch) =>
    ch === "：" ? ":" : String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30)
  );
}

/**
 * 识别结果分两档强度 —— 0831 裁决(爱民 3:4 被「正方形模块」盖掉后重定):
 * - `explicit`(文案里写了数字比例「9:16」):与手选不一致时**不静默压**,交调用方
 *   弹确认卡问用户(见 resolveRatioRuling.conflictingExplicit);没有手选时直接生效。
 * - `keyword`(方向/平台词面「竖版」「小红书」):只在**没有手选**时生效 ——
 *   词面是猜的,手选是用户亲手点的,猜的不许压亲手点的、也不配为它弹卡。
 */
export interface RatioDetection {
  ratio: Ratio;
  source: "explicit" | "keyword";
}

/** 分强度识别。显式 token 优先于关键词;latin 大小写不敏感;全角冒号/数字先归一化。 */
export function detectRatioDetailedFromText(text: string): RatioDetection | null {
  if (!text || !text.trim()) return null;
  const normalized = normalizeFullwidth(text);
  for (const [re, ratio] of EXPLICIT) {
    if (re.test(normalized)) return { ratio, source: "explicit" };
  }
  const lower = normalized.toLowerCase();
  for (const [words, ratio] of KEYWORDS) {
    if (words.some((w) => lower.includes(w.toLowerCase()))) return { ratio, source: "keyword" };
  }
  if (GATED_SQUARE.test(lower)) return { ratio: "1:1", source: "keyword" };
  return null;
}

/** 兼容旧签名:只要比例不要强度的调用方仍走这里。 */
export function detectRatioFromText(text: string): Ratio | null {
  return detectRatioDetailedFromText(text)?.ratio ?? null;
}

/**
 * 从用户可表达画幅意图的两个字段(主文案 + 「特别要求」备注)里识别显式比例。
 *
 * 2026-07-17 —「特别要求(备注)」是用户写画幅意图的高频落点(如「9:16尺寸」),但旧提交
 * 逻辑只扫主文案 → 漏识别 → 退默认 1:1(生产实锤:杨海玉 13261004019)。两字段拼一起扫。
 */
export function detectRatioFromInputs(
  promptText: string | null | undefined,
  extraConstraint: string | null | undefined
): Ratio | null {
  return detectRatioDetailedFromInputs(promptText, extraConstraint)?.ratio ?? null;
}

/** 同 detectRatioFromInputs,但带强度(explicit/keyword)。 */
export function detectRatioDetailedFromInputs(
  promptText: string | null | undefined,
  extraConstraint: string | null | undefined
): RatioDetection | null {
  const combined = [promptText, extraConstraint]
    .filter((s): s is string => !!s && s.trim().length > 0)
    .join("\n");
  return detectRatioDetailedFromText(combined);
}

/**
 * 画幅裁决的**唯一**一份口径 —— 提交(useFreeCreateActions.ratioForReq)、画幅框
 * (FreeCreateBody.mirrorRatio)、底栏(effectiveRatioLabel)、确认卡触发
 * (requestGenerate.conflictRatio)四处共用。
 *
 * 0831 之前各处各裁各的,真机漂移(爱民 任务 5492005929984):手选 3:4、底栏回显 3:4
 * (底栏先看手选),提交侧却「文案一律最高」发了词面推的 1:1;连确认卡问完、
 * setRatio+touched 落定之后,提交侧照样再拿文案盖一遍 ——「不会再判一次」的注释是假的。
 *
 * 裁决序(取代 2026-05-25 版「文案一律最高」):
 *
 *   pinned(手选 / i2i 跟随原图 / 确认卡确认过的值) > 显式数字比例 > 语境化词面 > null
 *
 * - **裁决本身永不静默压 pinned**。显式数字比例与 pinned 不一致时,通过
 *   `conflictingExplicit` 交给调用方 —— 能弹卡的入口(单图自由创作)拿它去弹
 *   「画幅对不上,按哪个出?」,用户答完回填成 pinned,天然生效;弹不了卡的入口
 *   (i2i/组图/套图)按 pinned 走,与显示口径一致。
 * - 词面(keyword)是猜的,猜的不配质疑亲手点的:不进 conflict、不弹卡、直接输。
 * - null = 这里定不了,交调用方下一层(提交侧 LLM 5 选 1;显示侧 3:4 占位)。
 */
export function resolveRatioRuling(args: {
  detection: RatioDetection | null;
  /** 手选(ratioUserTouched)或 i2i 跟随原图时的那个值;两者都不是则 null。 */
  pinnedRatio: Ratio | null;
}): { ratio: Ratio | null; conflictingExplicit: Ratio | null } {
  const { detection, pinnedRatio } = args;
  if (pinnedRatio) {
    return {
      ratio: pinnedRatio,
      conflictingExplicit:
        detection?.source === "explicit" && detection.ratio !== pinnedRatio
          ? detection.ratio
          : null,
    };
  }
  return { ratio: detection?.ratio ?? null, conflictingExplicit: null };
}
